/**
 * Per-module PDP section flags — PIP lean rebuild WS0.6.
 *
 * Splits the single SHOW_PREVIEW_SECTIONS gate that lived in
 * app/products/[slug]/product-client.tsx into one env flag per enrichment
 * module, so the owner can flip each module independently (WS7 go-live
 * evidence packs) and WS1-T2's thousands of new fin_videos rows can't surface
 * ungated in any preview/override context.
 *
 *   NEXT_PUBLIC_MODULE_VIDEOS   VideoReviews
 *   NEXT_PUBLIC_MODULE_XRETAIL  CrossRetailerPriceWidget
 *   NEXT_PUBLIC_MODULE_REVIEWS  CustomerReviews + ReviewTopicFilter
 *   NEXT_PUBLIC_MODULE_PDFS    ProductDocuments
 *   NEXT_PUBLIC_MODULE_FAQ     AutoFAQ
 *   NEXT_PUBLIC_MODULE_COMPARE  Inline Mini-Comparison (§32) + Product Identifiers (§33)
 *   NEXT_PUBLIC_MODULE_GALLERY  getDisplayGallery on the PDP hero gallery
 *                               (server-side in page.tsx, not product-client;
 *                               unlike the section flags this swaps the IMAGE
 *                               SET of an always-on module — the resolver's
 *                               byte-identical fallback keeps flag-on safe for
 *                               curated/enriched products)
 *   NEXT_PUBLIC_MODULE_PRICE_WATCH      PriceWatchForm (PDP offers card)
 *   NEXT_PUBLIC_MODULE_RECALL_ALERT     RecallAlertForm (PDP, below RecallBanner)
 *   NEXT_PUBLIC_MODULE_FILTER_REMINDER  FilterReminderForm (water-filter-finder tool)
 *   NEXT_PUBLIC_MODULE_FAQ_CTA          Spec-FAQ "Compare N offers for this model" row
 *
 * Semantics per flag:
 *   '1'   → force the module ON (every environment)
 *   '0'   → force the module OFF (every environment)
 *   unset → fall back to the module's PRE-SPLIT behavior, so absolutely
 *           nothing changes until an env var is set:
 *             - the preview-gated modules fall back to the legacy combined
 *               gate (NEXT_PUBLIC_SHOW_ENRICHMENT + NODE_ENV development);
 *             - AutoFAQ was NEVER preview-gated (it always rendered, with its
 *               own Real-or-Hidden data gate), so its call site passes
 *               `fallback: true` to preserve always-on when unset.
 *
 * Real-or-Hidden data gates INSIDE the components are untouched — these flags
 * only decide whether a module participates in the page at all.
 *
 * BUILD-TIME INLINING CAVEAT: Next.js exposes NEXT_PUBLIC_* values to client
 * bundles by literally string-replacing `process.env.NEXT_PUBLIC_X` MEMBER
 * EXPRESSIONS at build time. Dynamic lookups (`process.env[name]`) are NOT
 * replaced and read as undefined in the browser — hence the literal switch
 * below (do not "simplify" it into a map keyed by name built from strings).
 * It also means changing any of these flags requires a REBUILD/REDEPLOY, not
 * just an env edit against a running deployment.
 */

export type PdpModuleName =
  | 'VIDEOS'
  | 'XRETAIL'
  | 'REVIEWS'
  | 'PDFS'
  | 'FAQ'
  | 'COMPARE'
  | 'GALLERY'
  | 'SPECS'
  | 'VERDICT'
  | 'SPEC_FAQ_LEGACY'
  | 'PRICE_WATCH'
  | 'RECALL_ALERT'
  | 'FILTER_REMINDER'
  | 'FAQ_CTA'

/** Raw env value for a module flag. Literal member reads only (see caveat). */
function rawModuleEnv(name: PdpModuleName): string | undefined {
  switch (name) {
    case 'VIDEOS':
      return process.env.NEXT_PUBLIC_MODULE_VIDEOS
    case 'XRETAIL':
      return process.env.NEXT_PUBLIC_MODULE_XRETAIL
    case 'REVIEWS':
      return process.env.NEXT_PUBLIC_MODULE_REVIEWS
    case 'PDFS':
      return process.env.NEXT_PUBLIC_MODULE_PDFS
    case 'FAQ':
      return process.env.NEXT_PUBLIC_MODULE_FAQ
    case 'COMPARE':
      return process.env.NEXT_PUBLIC_MODULE_COMPARE
    case 'GALLERY':
      return process.env.NEXT_PUBLIC_MODULE_GALLERY
    case 'SPECS':
      return process.env.NEXT_PUBLIC_MODULE_SPECS
    case 'VERDICT':
      return process.env.NEXT_PUBLIC_MODULE_VERDICT
    case 'SPEC_FAQ_LEGACY':
      // FAQ-rewrite recovery flag (docs/Page-Types-And-Templates.md §3.3).
      // INVERTED default vs the other module flags: unset = OFF (the rewrite
      // is the default); '1' resurrects the legacy templated ProductFAQ
      // (visible copy only — its JSON-LD stays retired). Owner decision
      // 2026-07-10: prune by default, keep recovery available >=90 days.
      return process.env.NEXT_PUBLIC_MODULE_SPEC_FAQ_LEGACY
    // PRICE_WATCH relaunched (masterplan item 181, owner-directed): its PDP
    // call site now passes `fallback: true`, so unset = ON everywhere and
    // '0' + redeploy is the kill switch. The 2026-07-11 pause predated the
    // live email stack — the double-opt-in confirm now sends transactionally.
    case 'PRICE_WATCH':
      return process.env.NEXT_PUBLIC_MODULE_PRICE_WATCH
    // Still-paused signup/CTA surfaces (owner hotfix 2026-07-11): call sites
    // pass no fallback, so unset = dev-preview only (legacy gate) and the
    // code stays exercisable in development; '1' + redeploy relaunches in
    // prod; '0' force-hides everywhere.
    case 'RECALL_ALERT':
      return process.env.NEXT_PUBLIC_MODULE_RECALL_ALERT
    case 'FILTER_REMINDER':
      return process.env.NEXT_PUBLIC_MODULE_FILTER_REMINDER
    case 'FAQ_CTA':
      return process.env.NEXT_PUBLIC_MODULE_FAQ_CTA
  }
}

/**
 * The legacy combined preview gate — same semantics as the retired
 * SHOW_PREVIEW_SECTIONS const (2026-06-20): shown in local dev (or wherever
 * NEXT_PUBLIC_SHOW_ENRICHMENT=1), hidden in production;
 * NEXT_PUBLIC_SHOW_ENRICHMENT=0 force-hides even in dev.
 */
export function legacyPreviewGate(): boolean {
  return (
    process.env.NEXT_PUBLIC_SHOW_ENRICHMENT !== '0' &&
    (process.env.NEXT_PUBLIC_SHOW_ENRICHMENT === '1' ||
      process.env.NODE_ENV === 'development')
  )
}

/**
 * Per-module visibility. '1' force-on, '0' force-off, anything else (incl.
 * unset) → `fallback` when provided, otherwise the legacy preview gate.
 */
export function moduleFlag(name: PdpModuleName, fallback?: boolean): boolean {
  const v = rawModuleEnv(name)
  if (v === '1') return true
  if (v === '0') return false
  return fallback ?? legacyPreviewGate()
}
